import { inject, Injectable } from '@angular/core';
import { catchError, first, forkJoin, Observable, of, switchMap } from 'rxjs';
import * as uuid from 'uuid';
import { SOCKET_ENTRY_EVENT } from '../../enums/events-buffer/socket/socket-entry-event.enum';
import { IAudit, IAuditBody } from '../../interfaces/audit.interface';
import { IDevice } from '../device/models/device.model';
import { Entry } from './domain/model/entry.model';
import { Logger } from '../../interfaces/Logger';
import { IOrganization } from '../organization/domain/interfaces/organization.interface';
import { AuditService } from '../audit/audit.service';
import { DeviceService } from '../device/device.service';
import { OrganizationRepositoryAdapter } from '../organization/infrastructure/adapters/organization-repository.adapter';
import { EntryMapper } from './infrastructure/mapper/entry.mapper';

import { SERVICE_STATE_SERVICE } from '@features/service/domain/tokens/service-state.token';
import { Worker } from '@features/service/domain/models/worker.model';

/**
 * Service responsible for handling audit events related to account entries.
 *
 * This service centralizes the logic for logging, updating, and removing audit events
 * for account entries, ensuring that all audit operations are performed consistently
 * and with the required contextual data (device, worker, organization, etc.).
 *
 * @remarks
 * - Utilizes dependency injection for all required services.
 * - Handles error logging and fallback values gracefully.
 * - All audit operations are performed asynchronously and return observables.
 *
 * @example
 * // To log the addition of a new entry:
 * entryAuditHandlerService.auditAddNewEntryToAccountEvent(entry).subscribe();
 *
 * @see IAudit
 * @see IAuditBody
 * @see Entry
 */
@Injectable({
  providedIn: 'root',
})
export class EntryAuditHandlerService {
  private readonly organizationService = inject(OrganizationRepositoryAdapter);
  private readonly auditService = inject(AuditService);
  private readonly deviceService = inject(DeviceService);
  private readonly logger = inject(Logger);

  private readonly serviceStateService = inject(SERVICE_STATE_SERVICE);

  /**
   * Logs an audit event for adding a new account entry.
   *
   * @param entry - The entry to log.
   * @returns Observable with the audit object or `null` on error.
   * @remarks
   * - The audit body is constructed with device and worker context.
   * - Errors are logged and result in a null observable.
   */
  auditAddNewEntryToAccountEvent(entry: Entry): Observable<IAudit | null> {
    return this.getAuditBodyData().pipe(
      first(),
      switchMap(({ device, currentWorker }) => {
        if (!device || !device._id) {
          this.logger.error(new Error('[EntryAuditHandler][auditAddNewEntryToAccountEvent] Invalid device data:'), device);

          return of(null);
        }

        const auditBody: IAuditBody = {
          _id: uuid.v4(),
          deviceId: device._id,
          action: SOCKET_ENTRY_EVENT.create,
          authorId: currentWorker?.id || '',
          timestamp: Date.now(),
          payload: EntryMapper.toApi(entry),
        };

        return this.auditService.insertAuditBodyInCurrentAuditAccount(auditBody);
      }),
      catchError((error) => {
        this.logger.error(
          new Error('[EntryAuditHandler][auditAddNewEntryToAccountEvent] Error inserting new audit body to new Entry'),
          error
        );

        return of(null);
      })
    );
  }

  /**
   * Updates the audit event for a modified entry.
   *
   * @param entry - The updated entry.
   * @returns An Observable emitting the updated IAudit or null on error.
   * @remarks
   * - The audit body includes only the relevant updated fields.
   * - Errors are logged and result in a null observable.
   */
  auditUpdateEntryUnitEvent(entry: Entry): Observable<IAudit | null> {
    return this.getAuditBodyData().pipe(
      first(),
      switchMap(({ device, currentWorker }) => {
        if (!device || !device._id) {
          this.logger.error(new Error('Invalid device data:'), device);

          return of(null);
        }

        if (!entry || !entry._id) {
          this.logger.error(new Error('Invalid entry data:'), entry);

          return of(null);
        }

        const auditBody: IAuditBody = {
          _id: uuid.v4(),
          deviceId: device._id,
          action: SOCKET_ENTRY_EVENT.update,
          authorId: currentWorker?.id || '',
          timestamp: Date.now(),
          payload: {
            entry_id: entry._id,
            initial_total: entry.initialTotal,
            quantity: entry.quantityToDisplay,
            unit_price_modified: entry.unitPriceModified,
            discount: entry.discount || null,
          },
        };

        return this.auditService.insertAuditBodyInCurrentAuditAccount(auditBody);
      }),
      catchError((error) => {
        this.logger.error(
          new Error('Error updating audit event for the entry:'),
          error
        );

        return of(null);
      })
    );
  }

  /**
   * Removes an entry unit event from the audit log.
   *
   * @param entry - The entry to be removed.
   * @returns An Observable emitting the updated IAudit or null on error.
   * @remarks
   * - The audit body only includes the entry ID for removal.
   * - Errors are logged and result in a null observable.
   */
  auditRemoveEntryUnitEvent(entry: Entry): Observable<IAudit | null> {
    return this.getAuditBodyData().pipe(
      first(),
      switchMap(({ device, currentWorker }) => {
        if (!device || !device._id) {
          this.logger.error(new Error('Invalid device data:'), device);

          return of(null);
        }

        if (!entry || !entry._id) {
          this.logger.error(new Error('Invalid entry data:'), entry);

          return of(null);
        }

        const auditBody: IAuditBody = {
          _id: uuid.v4(),
          deviceId: device._id,
          action: SOCKET_ENTRY_EVENT.delete,
          authorId: currentWorker?.id || '',
          timestamp: Date.now(),
          payload: {
            entry_id: entry._id,
          },
        };

        return this.auditService.insertAuditBodyInCurrentAuditAccount(auditBody);
      }),
      catchError((error) => {
        this.logger.error(
          new Error('Error removing audit event for the entry:'),
          error
        );

        return of(null);
      })
    );
  }

  /**
   * Gets the necessary data for the audit body.
   *
   * @returns An Observable that emits an object containing the organization, current worker,
   *          current audit, and device, or null in case of error.
   * @remarks
   * - Uses forkJoin to aggregate all required context data.
   * - Errors are logged and result in all fields being null.
   */
  private getAuditBodyData(): Observable<{
    organization: IOrganization | null;
    currentWorker: Worker | null;
    currentAudit: IAudit | null;
    device: IDevice | null;
  }> {
    return forkJoin({
      organization: this.organizationService.getCurrentOrganization(),
      currentWorker: of(this.serviceStateService.getCurrentWorker()),
      currentAudit: this.auditService.getCurrentAuditAccount(),
      device: this.deviceService.getDevice(),
    }).pipe(
      first(),
      catchError((error) => {
        this.logger.error(
          new Error('Error retrieving audit body data:'),
          error
        );

        return of({
          organization: null,
          currentWorker: null,
          currentAudit: null,
          device: null,
        });
      })
    );
  }
}
